import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Repeat, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { SidebarGroup } from './SidebarGroup';
import { SidebarItem } from './SidebarItem';
import styles from './Sidebar.module.css';

/**
 * Interfaces para el menú dinámico
 */
export interface MenuAcceso {
  id: number;
  nombre: string;
  url: string | null;
}

export interface MenuItem {
  id: number;
  nombre: string;
  descripcion?: string | null; // Permitir null para compatibilidad con TransformedMenuItem
  url: string | null;
  icono?: string | null;
  orden: number;
  padre_id: number | null;
  accesos: MenuAcceso[];
  children?: MenuItem[];
}

/**
 * Props del componente Sidebar
 * @param menuData - Array de MenuItem que representa la estructura del menú
 * @param isMobileOpen - Estado de apertura en móvil (controlado externamente)
 * @param onMobileClose - Callback para cerrar el sidebar en móvil
 * @param isLoading - Indica si el menú se está cargando
 * @param error - Mensaje de error si falla la carga del menú
 * @param onRetry - Callback para reintentar cargar el menú
 * @param onCollapseChange - Callback cuando cambia el estado de colapso del sidebar
 */
export interface SidebarProps {
  menuData: MenuItem[];
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onCollapseChange?: (isCollapsed: boolean) => void;
  onMenuItemClick?: (item: MenuItem) => void;
}

export const Sidebar = ({
  menuData,
  isMobileOpen = false,
  onMobileClose,
  isLoading = false,
  error = null,
  onRetry,
  onCollapseChange,
  onMenuItemClick
}: SidebarProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());
  const [initialized, setInitialized] = useState(false);
  const location = useLocation();

  // Cargar elementos abiertos desde localStorage y auto-expandir elementos activos al montar
  useEffect(() => {
    const expandActiveItems = (items: MenuItem[], acc: Set<number> = new Set()): Set<number> => {
      items.forEach(item => {
        if (item.url && location.pathname.startsWith(item.url)) {
          acc.add(item.id);
          // Expandir todos los padres
          let parent = items.find(i => i.id === item.padre_id);
          while (parent) {
            acc.add(parent.id);
            parent = items.find(i => i.id === parent!.padre_id);
          }
        }
        if (item.children) {
          expandActiveItems(item.children, acc);
        }
      });
      return acc;
    };

    // Cargar estado colapsado
    const savedCollapsed = localStorage.getItem('sidebar-collapsed');
    const collapsedState = savedCollapsed === 'true';
    if (savedCollapsed !== null) {
      setIsCollapsed(collapsedState);
      // Notificar al padre sobre el estado colapsado inicial
      onCollapseChange?.(collapsedState);
    }

    // Cargar elementos abiertos
    const saved = localStorage.getItem('sidebar-open-items');
    let savedItems = new Set<number>();

    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        savedItems = new Set(parsed);
      } catch (e) {
        console.error('Error cargando estado del sidebar:', e);
      }
    }

    // Combinar elementos guardados con elementos activos (los elementos activos tienen prioridad)
    const activeItems = expandActiveItems(menuData);
    const merged = new Set([...savedItems, ...activeItems]);

    setOpenItems(merged);
    setInitialized(true);
  }, []); // Solo ejecutar una vez al montar

  // Guardar elementos abiertos en localStorage cuando cambien (pero omitir primer renderizado)
  useEffect(() => {
    if (initialized) {
      localStorage.setItem('sidebar-open-items', JSON.stringify(Array.from(openItems)));
    }
  }, [openItems, initialized]);

  // Guardar estado colapsado en localStorage cuando cambie
  useEffect(() => {
    if (initialized) {
      localStorage.setItem('sidebar-collapsed', String(isCollapsed));
    }
  }, [isCollapsed, initialized]);

  // Efecto para manejar el hover: expandir cuando el mouse entra, colapsar cuando sale
  useEffect(() => {
    if (isHovered && isCollapsed) {
      // El sidebar se exppande temporalmente cuando hay hover
      // Esto se maneja con CSS
    }
  }, [isHovered, isCollapsed]);

  const handleToggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    onCollapseChange?.(newCollapsedState);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Auxiliar: Encontrar todos los elementos recursivamente
  const getAllItems = (items: MenuItem[]): MenuItem[] => {
    const result: MenuItem[] = [];
    const traverse = (itemList: MenuItem[]) => {
      itemList.forEach(item => {
        result.push(item);
        if (item.children) {
          traverse(item.children);
        }
      });
    };
    traverse(items);
    return result;
  };

  // Auxiliar: Obtener hermanos de un elemento
  const getSiblings = (itemId: number, parentId: number | null): number[] => {
    const allItems = getAllItems(menuData);
    return allItems
      .filter(item => item.padre_id === parentId && item.id !== itemId)
      .map(item => item.id);
  };

  // Auxiliar: Cerrar todos los descendientes de un elemento
  const closeDescendants = (itemId: number, openSet: Set<number>) => {
    const allItems = getAllItems(menuData);
    const children = allItems.filter(item => item.padre_id === itemId);
    children.forEach(child => {
      openSet.delete(child.id);
      closeDescendants(child.id, openSet);
    });
  };

  // Manejar toggle del acordeón
  const handleItemToggle = (itemId: number, parentId: number | null) => {
    setOpenItems(prev => {
      const newSet = new Set(prev);

      if (newSet.has(itemId)) {
        // Cerrar elemento clickeado y todos sus descendientes
        newSet.delete(itemId);
        closeDescendants(itemId, newSet);
      } else {
        // Cerrar todos los hermanos (comportamiento de acordeón)
        const siblings = getSiblings(itemId, parentId);
        siblings.forEach(siblingId => {
          newSet.delete(siblingId);
          closeDescendants(siblingId, newSet);
        });

        // Abrir elemento clickeado
        newSet.add(itemId);
      }

      return newSet;
    });
  };

  // Construir la estructura de menú agrupada
  const buildMenuStructure = (items: MenuItem[]): { groups: string[]; itemsByGroup: Record<string, MenuItem[]> } => {
    const groups: string[] = [];
    const itemsByGroup: Record<string, MenuItem[]> = {};

    // Por ahora, agrupamos todos los items en un grupo "Main"
    // En producción, podrías tener lógica más compleja basada en padre_id
    const rootItems = items.filter(item => item.padre_id === null);

    if (rootItems.length > 0) {
      groups.push('Main');
      itemsByGroup['Main'] = rootItems;
    }

    return { groups, itemsByGroup };
  };

  const { groups, itemsByGroup } = buildMenuStructure(menuData);

  // Renderizar recursivamente los items del menú
  const renderMenuItem = (item: MenuItem, level: number = 0): React.ReactNode => {
    const hasChildren = item.children && item.children.length > 0;
    const shouldHideChildren = isCollapsed && !isHovered;
    const isOpen = openItems.has(item.id);

    // Handler combinado: cerrar móvil + callback de tabs
    const handleNavigate = () => {
      onMobileClose?.(); // Cerrar sidebar móvil
      if (item.url) {
        onMenuItemClick?.(item); // Callback para abrir tab
      }
    };

    return (
      <SidebarItem
        key={item.id}
        id={item.id}
        nombre={item.nombre}
        url={item.url}
        icono={item.icono || null}
        childrenData={item.children} // ✅ Pasar datos de hijos para calcular hasActiveChild
        hasChildren={hasChildren} // ✅ FIX: Pasar basado en datos, no en children renderizados
        isCollapsed={isCollapsed && !isHovered} // Para rendering (ocultar/mostrar texto)
        isHovered={isHovered}
        level={level}
        isOpen={isOpen}
        onToggle={handleItemToggle}
        onNavigate={handleNavigate} // ✅ Handler combinado
        parentId={item.padre_id}
      >
        {hasChildren && !shouldHideChildren && item.children?.map(child => renderMenuItem(child, level + 1))}
      </SidebarItem>
    );
  };

  const sidebarClasses = [
    styles.sidebar,
    styles.sidebarDark,
    styles.sidebarMain,
    isCollapsed ? styles.sidebarCollapsed : '',
    isHovered && isCollapsed ? styles.sidebarHovered : '',
    isMobileOpen ? styles.sidebarMobileOpen : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      {/* Overlay para móvil - solo renderizar cuando sidebar está abierto */}
      {isMobileOpen && (
        <div
          onClick={onMobileClose}
          className={styles.sidebarOverlay}
          aria-hidden="true"
        />
      )}

      <div
        className={sidebarClasses}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className={styles.sidebarContent}>
          {/* Sidebar header */}
          <div className={styles.sidebarSection}>
            <div className={styles.sidebarSectionBody}>
              <h5 className={`${styles.sidebarTitle} ${isCollapsed && !isHovered ? styles.hidden : ''}`}>
                Navegación
              </h5>

              <div className={styles.sidebarControls}>
                {/* Botón para colapsar/expandir (solo desktop) */}
                <button
                  type="button"
                  className={`${styles.sidebarControlButton} ${styles.sidebarResizeButton}`}
                  onClick={handleToggleCollapse}
                  aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  <Repeat size={16} />
                </button>

                {/* Botón para cerrar en móvil */}
                <button
                  type="button"
                  className={`${styles.sidebarControlButton} ${styles.sidebarMobileCloseButton}`}
                  onClick={onMobileClose}
                  aria-label="Close sidebar"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Main navigation */}
          <div className={styles.sidebarSection}>
            {isLoading ? (
              <div className={styles.sidebarLoading}>
                <Loader2 className={styles.loadingSpinner} size={24} />
                <p className={styles.loadingText}>Cargando menú...</p>
              </div>
            ) : error ? (
              <div className={styles.sidebarError}>
                <AlertCircle className={styles.errorIcon} size={24} />
                <p className={styles.errorText}>{error}</p>
                {onRetry && (
                  <button
                    type="button"
                    onClick={onRetry}
                    className={styles.retryButton}
                    aria-label="Reintentar cargar menú"
                  >
                    <RefreshCw size={16} />
                    <span>Reintentar</span>
                  </button>
                )}
              </div>
            ) : menuData.length === 0 ? (
              <div className={styles.sidebarEmpty}>
                <p className={styles.emptyText}>No hay elementos en el menú</p>
              </div>
            ) : (
              <ul className={styles.navSidebar}>
                {groups.map((groupName) => (
                  <React.Fragment key={groupName}>
                    <SidebarGroup
                      title={groupName}
                      isCollapsed={isCollapsed && !isHovered}
                      isHovered={isHovered}
                    />
                    {itemsByGroup[groupName]?.map((item) => renderMenuItem(item, 0))}
                  </React.Fragment>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

